import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string, userType?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Helper to update last login asynchronously
    const updateLastLogin = (u: User) => {
      // Defer any Supabase calls to avoid deadlocks in the auth callback
      setTimeout(async () => {
        try {
          await supabase
            .from('profiles')
            .update({
              last_login_at: new Date().toISOString(),
              email: u.email,
              email_verified: Boolean((u as any).email_confirmed_at),
            })
            .eq('user_id', u.id);
        } catch (e) {
          // Avoid noisy logs in production
          if (import.meta.env.MODE !== 'production') {
            console.warn('updateLastLogin failed', e);
          }
        }
      }, 0);
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);

      if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && newSession?.user) {
        updateLastLogin(newSession.user);
      }
    });

    // THEN check for existing session (and proactively refresh if needed)
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session) {
        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (!error && data.session) {
            setSession(data.session);
            setUser(data.session.user);
            updateLastLogin(data.session.user);
            setLoading(false);
            return;
          }
        } catch (e) {
          // ignore
        }
      }
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) updateLastLogin(session.user);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    return { error };
  };
  const signUp = async (email: string, password: string, fullName: string, userType: string = 'utente') => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          user_type: userType,
        }
      }
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const value = {
    user,
    session,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};